import React, { useEffect, useState, useMemo, memo } from "react";
import {
  ProductCardProductsType,
  ProductCardIsinToNameType,
  ProductCardCompareLabelWeightsType,
} from "types/AssistantTypes";
import styles from "./productCompareCommonTable.module.scss";
import { useTranslation } from "react-i18next";
import colorsStore from "shared/store/colorsStore";
import { formatItalianNumber } from "utils/formatUtils";

type ProductCommonTableCompareProps = {
  data: ProductCardCompareLabelWeightsType[];
  allProducts: ProductCardProductsType;
  isinToName: ProductCardIsinToNameType;
  title?: string;
  subtitle: string;
  withPesoRow?: boolean;
  addPercentage?: boolean;
  marginTop?: number;
};

const ProductCommonTableCompareComponent: React.FC<
  ProductCommonTableCompareProps
> = ({
  data,
  allProducts,
  isinToName,
  title = null,
  subtitle,
  withPesoRow = false,
  addPercentage = false,
  marginTop = 0,
}) => {
  const { t } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const [columns, setColumns] = useState(3);
  const rows = Array.from(
    { length: data.length + 1 },
    (_, rowIndex) => rowIndex + 1,
  );

  useEffect(() => {
    //** set number of columns based on the number of product we want to compare */
    if (allProducts) {
      const productCount = Object.keys(allProducts).length;
      setColumns(productCount + 1);
    }
  }, [allProducts]);

  const cells = useMemo(
    () =>
      rows.flatMap((row) =>
        Array.from({ length: columns }, (_, colIndex) => ({
          row,
          col: colIndex + 1,
        })),
      ),
    [rows, columns],
  );

  const dashValue = (dataValue: any): any => {
    if (!dataValue || dataValue === undefined) {
      return "-";
    }
    if (dataValue === "n.a.") return dataValue;
    if (addPercentage) {
      return `${formatItalianNumber(dataValue, 2)}%`;
    }
    return formatItalianNumber(dataValue, 2);
  };

  const renderedCells = useMemo(() => {
    return cells.map((cell) => {
      const { row, col } = cell;

      if (row === 1) {
        if (col === 1) {
          return (
            <div key={`${row}-${col}`} className={[styles.titleCell].join(" ")}>
              {subtitle}
            </div>
          );
        }
        const isin = allProducts[col - 2];
        const product = isinToName[isin];
        return (
          <div
            key={`${row}-${col}`}
            className={[styles.cell, styles.productName].join(" ")}
          >
            {product}
          </div>
        );
      }
      if (row > 1) {
        if (col === 1 && row === 2) {
          return (
            <div
              key={`${row}-${col}`}
              className={[
                styles.cellKeys,
                styles.firstCellKey,
                styles.cellWithBorder,
              ].join(" ")}
            >
              <div style={{ paddingBottom: 7, backgroundColor: "" }}>
                {data[row - 2].label}
              </div>
            </div>
          );
        }
        if (col === 1 && row > 2) {
          return (
            <div
              key={`${row}-${col}`}
              className={[
                styles.cellKeys,
                row < data.length + 1
                  ? styles.cellWithBorder
                  : styles.cellWithNoBorder,
              ].join(" ")}
            >
              {data[row - 2].label}
            </div>
          );
        }

        if (col > 1 && row === 2) {
          return (
            <div key={`${row}-${col}`}>
              <div className={styles.cellValueInnerHeader}>
                {/* {data[row - 2].category_values[col - 2]} */}
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                    }}
                  >
                    {withPesoRow && (
                      <div className={styles.pad848}>
                        {t("productCard.peso")}
                      </div>
                    )}
                    <div
                      className={[
                        styles.cellValueInnerHeaderValue,
                        styles.cellWithBorder,
                      ].join(" ")}
                      style={{
                        textAlign: "center",
                        lineHeight: "40px",
                        color:
                          parseFloat(data[row - 2].weights[col - 2]) < 0
                            ? colorsConfig.red
                            : "inherit",
                      }}
                    >
                      {dashValue(data[row - 2].weights[col - 2])}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        if (
          col > 1 &&
          row - 2 < data.length &&
          row !== 2 &&
          col - 2 < data[row - 2].weights.length
        ) {
          return (
            <div key={`${row}-${col}`}>
              <div
                className={[
                  styles.cellValue,
                  styles.cellValueInner,
                  row < data.length + 1
                    ? styles.cellWithBorder
                    : styles.cellWithNoBorder,
                ].join(" ")}
              >
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                  }}
                >
                  <div
                    className={styles.pad8}
                    style={{
                      color:
                        parseFloat(data[row - 2].weights[col - 2]) < 0
                          ? colorsConfig.red
                          : "inherit",
                    }}
                  >
                    {dashValue(data[row - 2].weights[col - 2])}
                  </div>
                </div>
              </div>
            </div>
          );
        }
      }

      return (
        <div key={`${row}-${col}`} className={styles.cellValue}>
          -
        </div>
      );
    });
  }, [cells, allProducts, isinToName, data]);

  return (
    <div style={{ marginTop: marginTop }}>
      {title && <div className={styles.title}>{title}</div>}
      <div className={styles.container}>
        <div className={`${styles.grid} ${styles[`grid${columns}`]}`}>
          {renderedCells}
        </div>
      </div>
    </div>
  );
};

// Use React.memo to prevent re-renders if props haven't changed
export const ProductCommonTableCompare = memo(
  ProductCommonTableCompareComponent,
  (prevProps, nextProps) => {
    // Only re-render if data changes
    return JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data);
  },
);

export default ProductCommonTableCompare;
